import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Headphones, Mic, BookOpen, PenTool, Printer, 
  ArrowLeft, CheckCircle, XCircle, GraduationCap, Calendar, User, Download, Info, Brain, BarChart3
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { StandardsBadges } from "@/components/StandardsBadges";
import CanDoReference from "@/components/CanDoReference";
import StudentCrosswalkSuggestion from "@/components/StudentCrosswalkSuggestion";
import PriorAssessmentHistory from "@/components/PriorAssessmentHistory";
import MultipleMeasuresDisclaimer from "@/components/MultipleMeasuresDisclaimer";
import NPRPanel from "@/components/NPRPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResultDetail = () => {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [session, setSession] = useState(null);
  const [responses, setResponses] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const isTeacherOrAdmin = user?.role === 'teacher' || user?.role === 'school_admin' || user?.role === 'district_admin' || user?.role === 'super_admin';

  const modalities = [
    { key: "listening", label: "Listening", icon: Headphones, color: "bg-purple-100 text-purple-700 border-purple-300" },
    { key: "speaking", label: "Speaking", icon: Mic, color: "bg-blue-100 text-blue-700 border-blue-300" },
    { key: "reading", label: "Reading", icon: BookOpen, color: "bg-green-100 text-green-700 border-green-300" },
    { key: "writing", label: "Writing", icon: PenTool, color: "bg-orange-100 text-orange-700 border-orange-300" }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resultRes, sessionRes, responsesRes] = await Promise.all([
          axios.get(`${API}/results/${sessionId}`),
          axios.get(`${API}/exam-sessions/${sessionId}`),
          axios.get(`${API}/responses/${sessionId}`)
        ]);
        
        setResult(resultRes.data);
        setSession(sessionRes.data);
        setResponses(responsesRes.data.responses);
        
        // Fetch questions for this grade (with standards if teacher/admin)
        const standardsParam = isTeacherOrAdmin ? '&include_standards=true' : '';
        const questionsRes = await axios.get(`${API}/questions?grade_level=${sessionRes.data.grade_level}${standardsParam}`);
        setQuestions(questionsRes.data.questions);
      } catch (error) {
        console.error("Error fetching result:", error);
        toast.error("Failed to load result details");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [sessionId]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/results/${sessionId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `MOSAIC_Report_${result?.student_name?.replace(/\s+/g, '_') || 'student'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Failed to download PDF');
    }
  };

  const getProficiencyBadgeClass = (level) => {
    return `wida-badge level-${level}`;
  };

  const gradeLabels = {
    "K": "Kindergarten",
    "1": "Grade 1",
    "2": "Grade 2",
    "3-4": "Grades 3-4",
    "5-6": "Grades 5-6",
    "7-8": "Grades 7-8",
    "9-12": "Grades 9-12"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!result || !session) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12 text-center">
        <h2 className="text-xl font-bold mb-4">Result Not Found</h2>
        <Link to="/results">
          <Button className="btn-sharp-primary">Back to Results</Button>
        </Link>
      </div>
    );
  }

  const questionMap = {};
  questions.forEach(q => { questionMap[q.id] = q; });

  const responseMap = {};
  responses.forEach(r => { responseMap[r.question_id] = r; });

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="result-detail-page">
      {/* Print Header */}
      <div className="hidden print:block mb-8">
        <div className="text-center border-b-2 border-black pb-4">
          <h1 className="text-2xl font-bold">Mosaic Proficiency Score Report</h1>
          <h2 className="text-xl">{result.student_name} - {gradeLabels[result.grade_level]}</h2>
          <p className="text-sm text-gray-600 mt-2">Date: {new Date(result.scored_at).toLocaleDateString()}</p>
        </div>
      </div>

      {/* Screen Header */}
      <div className="no-print">
        <div className="flex items-center gap-3 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-[#002FA7]">Home</Link>
          <span>/</span>
          <Link to="/results" className="hover:text-[#002FA7]">Results</Link>
          <span>/</span>
          <span className="text-gray-900">{result.student_name}</span>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Score Report
              {result.is_preview && (
                <span className="ml-3 text-sm font-medium px-3 py-1 bg-amber-100 text-amber-700 border border-amber-300 rounded-full align-middle">
                  Teacher Preview
                </span>
              )}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-600">
              <span className="flex items-center gap-2">
                <User size={16} />
                {result.student_name}
              </span>
              <span className="flex items-center gap-2">
                <GraduationCap size={16} />
                {gradeLabels[result.grade_level]}
              </span>
              <span className="flex items-center gap-2">
                <Calendar size={16} />
                {new Date(result.scored_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Link to="/results">
              <Button variant="outline" className="btn-sharp h-12">
                <ArrowLeft size={18} className="mr-2" />
                Back
              </Button>
            </Link>
            <Button onClick={handlePrint} className="btn-sharp-primary h-12">
              <Printer size={18} className="mr-2" />
              Print Report
            </Button>
            <Button onClick={handleDownloadPdf} variant="outline" className="btn-sharp h-12" data-testid="download-pdf-btn">
              <Download size={18} className="mr-2" />
              Download PDF
            </Button>
          </div>
        </div>
      </div>

      {/* MOSAIC-NY1 · Pillar 4 · Multiple-Measures Disclaimer */}
      <MultipleMeasuresDisclaimer variant="full" className="mb-6" />

      {/* Overall WIDA Score */}
      <div className="border-2 border-black p-8 mb-8 text-center" data-testid="overall-score-panel">
        <h2 className="text-lg font-semibold text-gray-500 mb-4">OVERALL PROFICIENCY</h2>        <div className="flex items-center justify-center gap-8">
          <div>
            <div className="text-6xl font-bold text-[#002FA7]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {result.overall_score}
            </div>
            <div className="text-gray-500 mt-2">Overall Scale Score</div>
          </div>
          <div className="h-24 w-px bg-gray-300"></div>
          <div>
            <div className={`${getProficiencyBadgeClass(result.wida_level)} text-2xl`}>
              <span className="text-4xl font-bold mr-2">{result.wida_level}</span>
              <span>{result.wida_level_name}</span>
            </div>
            <div className="text-gray-500 mt-2">Proficiency Level</div>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          {result.overall_score < 3.0 && "Beginning English learner"}
          {result.overall_score >= 3.0 && result.overall_score < 5.0 && "Intermediate / progressing English learner"}
          {result.overall_score >= 5.0 && "Advanced English learner, nearing or meeting proficiency"}
        </div>
      </div>

      {/* Domain Scores (WIDA Scale) */}
      <div className="border-2 border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Domain Scale Scores
          </h3>
          <p className="text-sm text-gray-500 mt-1">Each domain scored on the 1.0 - 6.0 proficiency scale</p>
        </div>
        <div className="results-grid">
          {modalities.map((modality) => {
            const rawScore = result[`${modality.key}_score`];
            const rawTotal = result[`${modality.key}_total`];
            const widaScore = result[`${modality.key}_wida`] || 1.0;
            const barPct = Math.min(((widaScore - 1) / 5) * 100, 100);
            const ModalityIcon = modality.icon;
            
            return (
              <div key={modality.key} className="results-card" data-testid={`modality-score-${modality.key}`}>
                <div className={`w-12 h-12 ${modality.color.split(' ')[0]} ${modality.color.split(' ')[1]} flex items-center justify-center mx-auto mb-3`}>
                  <ModalityIcon size={24} />
                </div>
                <div className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {widaScore}
                </div>
                <div className="results-label">{modality.label}</div>
                <Progress value={barPct} className="h-2 mt-3" />
                <div className="text-xs text-gray-500 mt-1">Raw: {rawScore}/{rawTotal}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Composite Scores */}
      <div className="border-2 border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Composite Scores
          </h3>
          <p className="text-sm text-gray-500 mt-1">Combined domain scores as reported on official WIDA ACCESS reports</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-200" data-testid="composite-scores">
          {[
            { label: "Oral Language", value: result.oral_language, desc: "Listening + Speaking" },
            { label: "Literacy", value: result.literacy, desc: "Reading + Writing" },
            { label: "Comprehension", value: result.comprehension, desc: "Listening + Reading" },
            { label: "Overall", value: result.overall_score, desc: "Weighted composite" },
          ].map((comp) => (
            <div key={comp.label} className="p-5 text-center" data-testid={`composite-${comp.label.toLowerCase().replace(' ', '-')}`}>
              <div className="text-2xl font-bold text-[#002FA7]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {comp.value || "—"}
              </div>
              <div className="font-semibold text-sm mt-1">{comp.label}</div>
              <div className="text-xs text-gray-400 mt-1">{comp.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Prior Assessment History (renders only when student has prior scores) */}
      {result?.student_id && <PriorAssessmentHistory studentId={result.student_id} />}

      {/* National Proficiency Reference (NPR v2.3 — qualitative, no numerical conversion) */}
      <NPRPanel
        grade={result.grade_level}
        scores={{
          listening: result.listening_wida,
          speaking:  result.speaking_wida,
          reading:   result.reading_wida,
          writing:   result.writing_wida,
        }}
      />

      {/* Important Note about varying domain scores */}
      <div className="bg-blue-50 border-2 border-blue-200 p-5 mb-8 text-sm" data-testid="score-interpretation-note">
        <p className="font-semibold text-blue-800 mb-1">Score Interpretation</p>
        <p className="text-blue-700">
          A student can have different scores in each domain. For example, a student may score higher in Listening (receptive skill) than in Writing (productive skill). This is very common and helps teachers target specific language support areas.
        </p>
      </div>

      {/* Response Details */}
      <div className="border-2 border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Response Details
          </h3>
        </div>
        
        {modalities.map((modality) => {
          const modalityQuestions = questions.filter(q => q.modality === modality.key);
          const ModalityIcon = modality.icon;
          
          if (modalityQuestions.length === 0) return null;
          
          return (
            <div key={modality.key} className="border-b border-gray-200 last:border-b-0">
              {/* Modality Header */}
              <div className="flex items-center gap-3 p-4 bg-gray-50 border-b border-gray-200">
                <div className={`w-8 h-8 ${modality.color.split(' ')[0]} ${modality.color.split(' ')[1]} flex items-center justify-center`}>
                  <ModalityIcon size={16} />
                </div>
                <span className="font-semibold">{modality.label}</span>
                <span className="text-sm text-gray-500">({modalityQuestions.length} questions)</span>
              </div>
              
              {/* Questions */}
              <div className="divide-y divide-gray-100">
                {modalityQuestions.map((question) => {
                  const response = responseMap[question.id];
                  const isCorrect = response?.is_correct;
                  const isAnswered = !!response;
                  
                  return (
                    <div key={question.id} className="p-4 flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 border border-gray-300 flex items-center justify-center text-sm font-semibold">
                        {question.question_number}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600 mb-1 truncate">{question.question_text}</p>
                        {isTeacherOrAdmin && (question.wida_standard || question.eld_standard) && (
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap" data-testid={`wida-tag-${question.id}`}>
                            <Info size={12} className="text-indigo-500" />
                            {question.wida_eld_level && (
                              <span className="text-xs text-indigo-600 font-medium">
                                ELD Level {question.wida_eld_level}
                              </span>
                            )}
                            <StandardsBadges eldStandard={question.eld_standard} klu={question.key_language_use} size="xs" />
                            {question.wida_mpi && (
                              <span className="text-xs text-indigo-400" title={question.wida_mpi}>
                                &mdash; {question.wida_mpi.length > 60 ? question.wida_mpi.substring(0, 60) + '...' : question.wida_mpi}
                              </span>
                            )}
                          </div>
                        )}
                        {isAnswered ? (
                          <div>
                            {question.question_type === "multiple_choice" ? (
                              <div className="flex items-center gap-2">
                                {isCorrect ? (
                                  <span className="inline-flex items-center gap-1 text-sm text-green-700">
                                    <CheckCircle size={14} />
                                    Correct
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-sm text-red-600">
                                    <XCircle size={14} />
                                    Incorrect
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm text-gray-500">
                                    Points: {response.points_earned}/{question.max_points}
                                    {response.graded_by && ` (Graded by: ${response.graded_by})`}
                                  </span>
                                </div>
                                {/* Automated Rubric Dimensional Scores */}
                                {response.ai_rubric_scores && (
                                  <div className="mt-2 bg-indigo-50 border border-indigo-200 p-3" data-testid={`ai-scores-${question.id}`}>
                                    <div className="flex items-center gap-1.5 mb-2">
                                      <Brain size={12} className="text-indigo-600" />
                                      <span className="text-xs font-semibold text-indigo-700">WIDA Rubric Analysis</span>
                                      <span className="text-xs text-indigo-400 ml-auto">
                                        Overall: L{response.ai_rubric_scores.overall_level}
                                      </span>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                      {[
                                        { key: 'linguistic_complexity', label: 'Ling. Complexity' },
                                        { key: 'vocabulary_usage', label: 'Vocabulary' },
                                        { key: 'language_control', label: 'Lang. Control' },
                                        ...(response.ai_rubric_scores.organization !== undefined ? [{ key: 'organization', label: 'Organization' }] : []),
                                      ].map(dim => {
                                        const val = response.ai_rubric_scores[dim.key];
                                        const bg = val >= 5 ? 'bg-green-100 text-green-700' : val >= 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
                                        return (
                                          <div key={dim.key} className={`px-2 py-1 text-xs font-semibold ${bg}`}>
                                            {dim.label}: {val}/6
                                          </div>
                                        );
                                      })}
                                    </div>
                                    {response.ai_rubric_scores.feedback && (
                                      <p className="text-xs text-indigo-600 mt-1.5">{response.ai_rubric_scores.feedback}</p>
                                    )}
                                  </div>
                                )}
                                {/* Show student's written/spoken answer */}
                                {response.student_answer && (question.modality === 'speaking' || question.modality === 'writing') && (
                                  <div className="mt-2 bg-gray-50 border border-gray-200 p-2">
                                    <div className="text-xs font-semibold text-gray-400 mb-1">
                                      {question.modality === 'speaking' ? 'Transcript' : 'Response'}:
                                    </div>
                                    <p className="text-xs text-gray-600 whitespace-pre-wrap">
                                      {response.student_answer.length > 200
                                        ? response.student_answer.substring(0, 200) + '...'
                                        : response.student_answer}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Not answered</span>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isAnswered && (
                          <span className={`inline-block w-8 h-8 flex items-center justify-center text-sm font-bold
                            ${question.question_type === "multiple_choice" 
                              ? (isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')
                              : 'bg-blue-100 text-blue-700'
                            }`}>
                            {response.points_earned}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* WIDA Level Interpretation */}
      <div className="border-2 border-gray-200 p-6">
        <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Proficiency Level {result.wida_level}: {result.wida_level_name}
        </h3>
        <div className="text-gray-600">
          {result.wida_level === 1 && (
            <p>Student is just beginning to understand and use English. Needs substantial language support. Focus on building foundational vocabulary and simple sentence structures.</p>
          )}
          {result.wida_level === 2 && (
            <p>Student can understand and use some social and academic English, but still needs strong support. Continue developing vocabulary and introduce more complex sentence patterns.</p>
          )}
          {result.wida_level === 3 && (
            <p>Student is building academic language and can participate more independently, though support is still needed. Focus on expanding academic vocabulary and discourse skills.</p>
          )}
          {result.wida_level === 4 && (
            <p>Student can use academic English in many situations and is becoming more independent. Continue building discipline-specific vocabulary and complex language structures.</p>
          )}
          {result.wida_level === 5 && (
            <p>Student is approaching grade-level English proficiency and can handle more complex language tasks. Focus on refining academic language and reducing errors.</p>
          )}
          {result.wida_level === 6 && (
            <p>Student demonstrates English proficiency comparable to a fluent academic English user. Continue supporting bilingualism — both languages are a strength.</p>
          )}
        </div>
      </div>

      {/* Can-Do Descriptors — Per-Modality Breakdown */}
      {isTeacherOrAdmin && result.wida_level && (
        <div className="border-2 border-gray-200 p-6" data-testid="result-cando-section">
          <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Can-Do Descriptors at Level {result.wida_level}
          </h3>
          <p className="text-sm text-gray-500 mb-4">What this student can do in each modality at their current level, with growth targets for the next level.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {['listening', 'speaking', 'reading', 'writing'].map(mod => (
              <CanDoReference key={mod} modality={mod} level={result.wida_level} compact={true} />
            ))}
          </div>
        </div>
      )}

      {/* Crosswalk Auto-Suggest — v6.25.0 */}
      {isTeacherOrAdmin && sessionId && (
        <StudentCrosswalkSuggestion sessionId={sessionId} />
      )}

      {/* Print Footer */}
      <div className="hidden print:block mt-8 pt-4 border-t border-gray-300 text-center text-sm text-gray-500">
        <p>MOSAIC Assessment • Score Report</p>
        <p className="text-xs mt-1">&copy; {new Date().getFullYear()} MOSAIC • Created by Lerno Co. • Generated {new Date().toLocaleDateString()}</p>
      </div>
    </div>
  );
};

export default ResultDetail;
